import { TAGBackground } from '@aspendental/themes';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Maybe, TextBlockSeo as TTextBlockSeo, TextBlockSeoContent } from '@/types/generated';
import TemplateContainer from '../TemplateContainer';
import { wrapperStyles, getContentWrapperStyles, getExpandArrowHolderStyles } from './TextBlockSEO.styles';
import ContentTypeRichText from '@shared-components/components/ContentTypeRichText';
import { DisableTemplatePaddingOn } from '@shared-components/types';
import { textToJumpLink } from '@utils';
import { incorrectUsageWarning } from '@utils/miscUtils';
import { resolveTemplatePadding } from '@utils/resolveTemplatePadding';
export const OpacityGenerator = ({
  divHeight,
  backgroundColor,
  isOpen
}: {
  divHeight: number;
  backgroundColor: TAGBackground;
  isOpen: boolean;
}) => {
  if (divHeight <= 150) return null;
  return <>
			{Array.from({
      length: 5
    }, (_, index) => index + 1).map(opacityLevel => {
      return <Box key={`opacity-${opacityLevel}`} sx={{
        backgroundColor: `backgrounds.${backgroundColor}`,
        height: `${(120 - opacityLevel * 20) / 16}rem`,
        display: isOpen ? 'none' : 'block',
        marginTop: `-${(120 - opacityLevel * 20) / 16}rem`,
        opacity: `.${opacityLevel}`,
        width: '100%'
      }} />;
    })}
		</>;
};
export interface ITextBlockSEO extends TTextBlockSeo {
  /**
   * Specifies the background color of the template.
   *
   * @type {Maybe<TAGBackground>}
   * @memberof ITextBlockSEO
   */
  backgroundColor?: Maybe<string>;
  /**
   * An option to disable padding on top and/or the bottom of the template.
   *
   * @type {Array<'Top' | 'Bottom'> | undefined}
   * @memberof ITextBlockSEO
   */
  disablePaddingOn?: DisableTemplatePaddingOn;
  /**
   * Rich text content
   *
   * @type {TextBlockSeoContent}
   * @memberof IMultiCard
   */
  content?: Maybe<TextBlockSeoContent>;
  /**
   * Used to identify a component on a page for the purpose of jumping to it using the anchor link.
   *
   * @type {Maybe<string>}
   * @memberof ITextBlockSEO
   */
  anchorId?: Maybe<string>;
}
export default function TextBlockSEO({
  backgroundColor,
  disablePaddingOn = [],
  content,
  anchorId
}: Readonly<ITextBlockSEO>) {
  const section = useRef<HTMLDivElement>(null);
  const [divHeight, setDivHeight] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));
  useEffect(() => {
    if (section.current) setDivHeight(section.current.scrollHeight);
  }, []);
  if (!content) {
    incorrectUsageWarning('TextBlockSEO', 'content is required');
    return null;
  }
  return <TemplateContainer containerSx={{
    ...resolveTemplatePadding(disablePaddingOn, isMedium),
    wrapperStyles
  }} backgroundColor={backgroundColor as TAGBackground} dataTestId="text_block_wrapper" {...anchorId && {
    id: textToJumpLink(anchorId)
  }} data-sentry-element="TemplateContainer" data-sentry-component="TextBlockSEO" data-sentry-source-file="TextBlockSEO.tsx">
			<Box ref={section} sx={getContentWrapperStyles(isOpen)} data-sentry-element="Box" data-sentry-source-file="TextBlockSEO.tsx">
				<ContentTypeRichText centerAlign disableGutters disablePaddingOn={['Top', 'Bottom']} content={content} contentMaxWidth="100%" backgroundColor="transparent" dataTestId="text_block_rich_text" data-sentry-element="ContentTypeRichText" data-sentry-source-file="TextBlockSEO.tsx" />
			</Box>

			<OpacityGenerator isOpen={isOpen} divHeight={divHeight} backgroundColor={backgroundColor as TAGBackground} data-sentry-element="OpacityGenerator" data-sentry-source-file="TextBlockSEO.tsx" />

			<Box sx={getExpandArrowHolderStyles(isOpen, divHeight)} data-sentry-element="Box" data-sentry-source-file="TextBlockSEO.tsx">
				<ExpandMoreIcon data-testid="icon_expand" fontSize="large" onClick={() => setIsOpen(!isOpen)} data-sentry-element="ExpandMoreIcon" data-sentry-source-file="TextBlockSEO.tsx" />
			</Box>
		</TemplateContainer>;
}